import { useState } from "react"
import { useCourierViewContext } from "../../../context/useCourierViewContext"
import { CourierAccountPopup } from "./CourierAccountPopup"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowRightFromBracket,
  faBell,
  faCircleUser,
  faMotorcycle,
  faPowerOff,
  faSpinner,
  faUser,
} from "@fortawesome/free-solid-svg-icons"
import { useConfirmation } from "../../../context/useConfirmationContext"
import logo from "../../../assets/images/Logo.png"
import { PlatformNoticeBell } from "../../../components/PlatformNotice/PlatformNoticeBell"
import { ERequestType } from "../../../Enum/ERequestType"

interface CourierNavbarProps {
  isOnline: boolean
  onToggleOnline: () => void
  isStatusUpdating?: boolean
  activeDeliveriesCount?: number
}

export const CourierNavbar = ({
  isOnline,
  onToggleOnline,
  isStatusUpdating = false,
  activeDeliveriesCount = 0,
}: CourierNavbarProps) => {
  const { auth, logout } = useCourierViewContext()

  const { setNewConfirmation } = useConfirmation()

  const { t } = useTranslation("frontend")

  const [isAccountPopupOpen, setIsAccountPopupOpen] = useState(false)

  const toggleLabel = isStatusUpdating
    ? t("delivery_common.processing")
    : isOnline
      ? t("delivery_common.online")
      : t("delivery_common.offline")

  const handleConfirmLogout = () => {
    setNewConfirmation(t("delivery_common.logout_title"), t("delivery_common.logout_description"), () => {
      logout()
    })
  }

  return (
    <>
      <Animate type={AnimationTypes.slideDownIn}>
        <nav className="sticky top-0 z-20 border-b-2 border-orange-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-3 py-3 md:px-6">
            <div className="min-w-0 flex items-center gap-2.5">
              <img src={logo} alt="Mestre da Comanda" className="w-[90px] max-[480px]:w-[100px]" />

              <div className="min-w-0">
                <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-orange-500">
                  Mestre da Comanda
                </p>

                <h1 className="truncate text-sm font-bold text-slate-800">
                  {t("delivery_common.hello")}, {auth.courier?.personEntity.name ?? t("delivery_common.courier")}
                </h1>
              </div>

              <span className="hidden rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-700 md:inline-flex">
                <FontAwesomeIcon icon={faMotorcycle} className="mr-1.5 mt-0.5" />
                {activeDeliveriesCount} {t("delivery_common.active_orders")}
              </span>
            </div>

            <div className="hidden items-center gap-2 md:flex">
              <PlatformNoticeBell requestType={ERequestType.Courier} />

              <button
                onClick={() => setIsAccountPopupOpen(true)}
                className="rounded-lg border border-orange-300 px-3 py-2 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierNavbar.button.setIsAccountPopupOpenTrue"
              >
                <FontAwesomeIcon icon={faCircleUser} className="mr-1.5" />
                {t("delivery_common.my_profile")}
              </button>

              <button
                onClick={onToggleOnline}
                disabled={isStatusUpdating}
                className={`rounded-lg px-3 py-2 text-xs font-semibold transition-all shadow-sm ${
                  isOnline
                    ? "bg-emerald-500 text-white hover:bg-emerald-600"
                    : "bg-orange-500 text-white hover:bg-orange-600"
                } ${isStatusUpdating ? "cursor-wait opacity-70" : ""}`}
                data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierNavbar.button.onToggleOnline"
              >
                <FontAwesomeIcon
                  icon={isStatusUpdating ? faSpinner : faPowerOff}
                  spin={isStatusUpdating}
                  className="mr-1.5"
                />
                {toggleLabel}
              </button>

              <button
                onClick={handleConfirmLogout}
                className="rounded-lg bg-red-500 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-red-600"
                data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierNavbar.button.handleConfirmLogout"
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} className="mr-1.5" />
                {t("delivery_common.logout")}
              </button>
            </div>
          </div>
        </nav>
      </Animate>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-orange-200 bg-white/95 px-3 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pt-2 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] md:hidden">
        <div className="grid grid-cols-5 gap-2">
          <button
            onClick={() => setIsAccountPopupOpen(true)}
            className="flex flex-col items-center justify-center rounded-xl border border-violet-200 bg-violet-50 px-2 py-2 text-[11px] font-semibold text-violet-700"
            data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierNavbar.button.setIsAccountPopupOpenTrue2"
          >
            <FontAwesomeIcon icon={faUser} className="mb-1 text-base" />
            {t("delivery_common.my_profile")}
          </button>

          <div className="flex items-center justify-center">
            <PlatformNoticeBell requestType={ERequestType.Courier} />
          </div>

          <div className="flex flex-col items-center justify-center rounded-xl border border-amber-200 bg-amber-50 px-2 py-2 text-[11px] font-bold text-amber-700 shadow-sm">
            <FontAwesomeIcon icon={faMotorcycle} className="mb-1 text-base text-amber-600" />
            {activeDeliveriesCount}
          </div>

          <button
            onClick={onToggleOnline}
            disabled={isStatusUpdating}
            className={`flex flex-col items-center justify-center rounded-xl px-2 py-2 text-[11px] font-semibold text-white ${
              isOnline ? "bg-emerald-500" : "bg-orange-500"
            } ${isStatusUpdating ? "cursor-wait opacity-70" : ""}`}
            data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierNavbar.button.onToggleOnline2"
          >
            <FontAwesomeIcon
              icon={isStatusUpdating ? faSpinner : faPowerOff}
              spin={isStatusUpdating}
              className="mb-1 text-base"
            />
            {toggleLabel}
          </button>

          <button
            onClick={handleConfirmLogout}
            className="flex flex-col items-center justify-center rounded-xl bg-red-500 px-2 py-2 text-[11px] font-semibold text-white"
            data-interaction-description-key="interactionDescriptions:pages.delivery.components.courierNavbar.button.handleConfirmLogout2"
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} className="mb-1 text-base" />
            {t("delivery_common.logout")}
          </button>
        </div>
      </div>

      <CourierAccountPopup isOpen={isAccountPopupOpen} onClose={() => setIsAccountPopupOpen(false)} />
    </>
  )
}
